import React, { useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Button,
  Alert,
  Box,
  Chip,
  useTheme,
} from '@mui/material';
import ReportProblemIcon from '@mui/icons-material/ReportProblem';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import axiosInstance from '../../Pages/axiosInstance';

interface ReportIssueDialogProps {
  open: boolean;
  onClose: () => void;
  deviceNumber: string | number;
  onSuccess?: (message: string) => void;
  onError?: (message: string) => void;
  onIssueReported?: () => void;
}

const ReportIssueDialog: React.FC<ReportIssueDialogProps> = ({
  open,
  onClose,
  deviceNumber,
  onSuccess,
  onError,
  onIssueReported
}) => {
  const theme = useTheme();
  const [issueDescription, setIssueDescription] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInventoryConfirmation, setShowInventoryConfirmation] = useState(false);
  const [inventoryMessage, setInventoryMessage] = useState('');

  const handleClose = () => {
    setIssueDescription('');
    setIsSubmitting(false);
    setShowInventoryConfirmation(false);
    setInventoryMessage('');
    onClose();
  };

  const handleSubmit = async (addToInventory?: boolean) => {
    if (!issueDescription.trim()) {
      onError?.('Please enter an issue description');
      return;
    }

    setIsSubmitting(true);
    try {
      const payload: any = {
        box: deviceNumber,
        comment: issueDescription,
      };

      if (addToInventory !== undefined) {
        payload.add_to_inventory = addToInventory;
      }

      await axiosInstance.post('/servicepanel/report_issue/', payload);

      onSuccess?.('Issue reported successfully');
      onIssueReported?.();
      handleClose();
    } catch (error: any) {
      console.error('Error reporting issue:', error);
      console.log('Error response data:', error.response?.data);
      console.log('Error detail:', error.response?.data?.detail);
      
      // Check if this is the inventory validation error - more flexible matching
      const errorDetail = error.response?.data?.detail;
      console.log('Checking error detail:', errorDetail);
      
      if (errorDetail && (
        errorDetail.includes('inventory') || 
        errorDetail.includes('Box nie znajduje się') ||
        errorDetail.includes('Czy chcesz dodać') ||
        errorDetail.toLowerCase().includes('inventory')
      )) {
        console.log('Inventory error detected, showing confirmation dialog');
        setInventoryMessage(errorDetail);
        setShowInventoryConfirmation(true);
        setIsSubmitting(false);
        return;
      }
      
      console.log('Not an inventory error, showing toast');
      onError?.(`Error reporting issue: ${error.response?.data?.detail || error.message || 'Unknown error'}`);
      setIsSubmitting(false);
    }
  };

  const handleInventoryConfirmation = (addToInventory: boolean) => {
    setShowInventoryConfirmation(false);
    handleSubmit(addToInventory);
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      fullWidth 
      maxWidth="md"
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          boxShadow: '0 10px 40px rgba(0,0,0,0.1)',
        }
      }}
    >
      <DialogTitle sx={{ 
        pb: 2, 
        display: 'flex', 
        alignItems: 'center', 
        gap: 2,
        background: `linear-gradient(135deg, ${theme.palette.error.main}15, ${theme.palette.error.main}05)`,
        borderBottom: `1px solid ${theme.palette.divider}`,
      }}>
        <ReportProblemIcon color="error" sx={{ fontSize: 28 }} />
        <Box>
          <Box sx={{ fontSize: '1.25rem', fontWeight: 600, color: 'text.primary' }}>
            Report Issue
          </Box>
          <Chip 
            label={`Device #${deviceNumber}`} 
            size="small" 
            color="error" 
            variant="outlined"
            sx={{ mt: 0.5, fontSize: '0.75rem' }}
          />
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ pt: 3 }}>
        <DialogContentText sx={{ mb: 3, fontSize: '0.95rem', color: 'text.secondary' }}>
          Please describe the issue with this device in detail. This will help our team resolve the problem quickly.
        </DialogContentText>
        
        {showInventoryConfirmation && (
          <Alert 
            severity="warning" 
            sx={{ 
              mb: 3,
              borderRadius: 2,
              '& .MuiAlert-icon': { fontSize: 22 },
              '& .MuiAlert-message': { fontSize: '0.95rem' }
            }}
            icon={<ReportProblemIcon />}
          >
            <Box sx={{ fontWeight: 500, mb: 1 }}>Device Not in Inventory</Box>
            <Box sx={{ mb: 1 }}>
              This device is not currently in the inventory. You can either:
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Add it to inventory and report the issue</li>
                <li>Report the issue without adding to inventory</li>
              </ul>
            </Box>
            <Box sx={{ fontSize: '0.85rem', fontStyle: 'italic', opacity: 0.8 }}>
              Original message: {inventoryMessage}
            </Box>
          </Alert>
        )}
        
        <TextField
          autoFocus
          margin="dense"
          label="Issue Description"
          placeholder="Describe the problem you're experiencing..."
          fullWidth
          variant="outlined"
          value={issueDescription}
          onChange={(e) => setIssueDescription(e.target.value)}
          multiline
          minRows={4}
          disabled={isSubmitting || showInventoryConfirmation}
          sx={{
            '& .MuiOutlinedInput-root': {
              borderRadius: 2,
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.error.main,
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: theme.palette.error.main,
              },
            },
          }}
        />
      </DialogContent>
      
      <DialogActions sx={{ 
        p: 3, 
        pt: 2, 
        gap: 1,
        borderTop: `1px solid ${theme.palette.divider}`,
      }}>
        {showInventoryConfirmation ? (
          <>
            <Button 
              onClick={handleClose} 
              disabled={isSubmitting}
              startIcon={<CancelIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 120,
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleInventoryConfirmation(false)} 
              variant="outlined"
              disabled={isSubmitting}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 160,
                borderColor: 'error.main',
                color: 'error.main',
                '&:hover': { 
                  borderColor: 'error.dark',
                  bgcolor: 'error.main',
                  color: 'white'
                },
              }}
            >
              Report without adding
            </Button>
            <Button 
              onClick={() => handleInventoryConfirmation(true)} 
              variant="contained"
              disabled={isSubmitting}
              startIcon={<CheckCircleIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 180,
                bgcolor: 'error.main',
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              Add to inventory & report issue
            </Button>
          </>
        ) : (
          <>
            <Button 
              onClick={handleClose} 
              disabled={isSubmitting}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 80,
              }}
            >
              Cancel
            </Button>
            <Button 
              onClick={() => handleSubmit()} 
              variant="contained"
              disabled={isSubmitting || !issueDescription.trim()}
              startIcon={isSubmitting ? null : <ReportProblemIcon />}
              sx={{ 
                borderRadius: 2,
                textTransform: 'none',
                fontWeight: 500,
                minWidth: 120,
                bgcolor: 'error.main',
                '&:hover': { bgcolor: 'error.dark' },
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Report Issue'}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ReportIssueDialog; 